import React, { useRef, useState } from 'react';
import { DeleteIcon, PlusIcon } from '../UI/Icons';
import { CuisineType } from '../../services/venueSettingsService';
import EmojiPicker from './EmojiPicker';
import styles from './CuisineEditor.module.css';

export interface CuisineEditorProps {
  value: CuisineType[];
  onChange: (next: CuisineType[]) => void;
  idErrors?: Record<number, string>;
}

function slugifyId(raw: string): string {
  return raw
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}

const DEFAULT_ICON = '🍽️';

const CuisineEditor: React.FC<CuisineEditorProps> = ({ value, onChange, idErrors = {} }) => {
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [pickerAnchor, setPickerAnchor] = useState<{ index: number; el: HTMLElement } | null>(null);
  const anchorRef = useRef<HTMLElement | null>(null);
  anchorRef.current = pickerAnchor?.el ?? null;

  const updateRow = (index: number, patch: Partial<CuisineType>) => {
    onChange(value.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  };

  const addRow = () => {
    onChange([...value, { id: '', label: '', icon: DEFAULT_ICON }]);
  };

  const removeRow = (index: number) => {
    onChange(value.filter((_, i) => i !== index));
    if (pickerAnchor?.index === index) setPickerAnchor(null);
  };

  const handleDragStart = (index: number) => {
    setDragIndex(index);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (dragIndex === null || dragIndex === index) return;

    const next = [...value];
    const [moved] = next.splice(dragIndex, 1);
    next.splice(index, 0, moved);
    onChange(next);
    setDragIndex(index);
  };

  const handleDragEnd = () => {
    setDragIndex(null);
  };

  return (
    <div className={styles.editor}>
      {value.length === 0 ? (
        <p className={styles.emptyHint}>No cuisine types yet. Add one to get started.</p>
      ) : (
        <div className={styles.headerRow} aria-hidden="true">
          <span className={styles.colDrag} />
          <span className={styles.colIcon}>Icon</span>
          <span className={styles.colId}>ID</span>
          <span className={styles.colLabel}>Label</span>
          <span className={styles.colActions} />
        </div>
      )}

      {value.map((row, index) => (
          <div
            key={`cuisine-${index}`}
            className={`${styles.row} ${dragIndex === index ? styles.rowDragging : ''}`}
            draggable
            onDragStart={() => handleDragStart(index)}
            onDragOver={(e) => handleDragOver(e, index)}
            onDragEnd={handleDragEnd}
          >
            <button
              type="button"
              className={styles.dragHandle}
              aria-label="Drag to reorder"
              tabIndex={-1}
            >
              ⋮⋮
            </button>

            <div className={styles.iconWrap}>
              <button
                type="button"
                className={styles.iconButton}
                onClick={(e) =>
                  setPickerAnchor(
                    pickerAnchor?.index === index ? null : { index, el: e.currentTarget },
                  )
                }
                aria-label="Pick icon"
              >
                {row.icon || DEFAULT_ICON}
              </button>
              {pickerAnchor?.index === index && (
                <EmojiPicker
                  value={row.icon || DEFAULT_ICON}
                  anchorRef={anchorRef}
                  onPick={(emoji) => updateRow(index, { icon: emoji })}
                  onClose={() => setPickerAnchor(null)}
                />
              )}
            </div>

            <div className={`${styles.fieldWrap} ${styles.colId}`}>
              <input
                type="text"
                className={`${styles.input} ${idErrors[index] ? styles.inputError : ''}`}
                value={row.id}
                onChange={(e) => updateRow(index, { id: slugifyId(e.target.value) })}
                placeholder="burgers"
                aria-label={`Cuisine id ${index + 1}`}
              />
              {idErrors[index] && <p className={styles.fieldError}>{idErrors[index]}</p>}
            </div>

            <div className={`${styles.fieldWrap} ${styles.colLabel}`}>
              <input
                type="text"
                className={styles.input}
                value={row.label}
                onChange={(e) => updateRow(index, { label: e.target.value })}
                placeholder="Burgers"
                aria-label={`Cuisine label ${index + 1}`}
              />
            </div>

            <button
              type="button"
              className={styles.deleteButton}
              onClick={() => removeRow(index)}
              aria-label="Delete cuisine type"
            >
              <DeleteIcon size={16} />
            </button>
          </div>
        ))}

      <button type="button" className={styles.addButton} onClick={addRow}>
        <PlusIcon size={16} />
        Add cuisine type
      </button>
    </div>
  );
};

export default CuisineEditor;
